import { http } from "./client";

// DTR: no employee submit. HR approves each employee's cut-off, then exports
// the approved ones for payroll; exported days are locked.
const data = (r) => r.data;
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

/** Filename from a Content-Disposition header, if any. */
const fileNameFrom = (headers, fallback) => {
  const header = headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : fallback;
};

export const dtrApi = {
  // GET /dtr/periods?year → { current, lastFinished, currentWeek, lastWeek, periods }
  getPeriods: (year) =>
    http.get("/dtr/periods", { params: clean({ year }) }).then(data),
  // GET /dtr/cutoff/progress?year&companyId&branchId&assignmentType → { year, current,
  // periods: [{ key, year, month, cutoff, label, isCurrent, total, processed,
  // state: NONE | OPEN | PARTIAL | PROCESSED }] } (cut-offs up to the current one)
  getCutoffProgress: (params) =>
    http.get("/dtr/cutoff/progress", { params: clean(params) }).then(data),
  // GET /dtr/weeks?date → { cutoff, weeks } (the weeks of the cut-off containing the date)
  getWeeks: (date) => http.get("/dtr/weeks", { params: { date } }).then(data),

  /* ---------- Cut-off per employee (HR): status, totals, days, approval ---------- */
  /**
   * GET /dtr/cutoff/employees?year&month&cutoff&companyId&branchId&assignmentType&search
   *   &withEntries=true|false|all (default true)&status=OPEN|APPROVED|EXPORTED&page&limit&includeDays
   * → { period, processOn, canApprove, data: [{ employee: { id, employeeNumber, name, branchId, branchName,
   *     assignmentType }, status, approvedBy, approvedByName, approvedAt, exportedAt, changedAfterApproval,
   *     totals, pendingCorrections, pendingOvertime, days? }], total, page, limit,
   *     summary: { employees, open, approved, exported, changedAfterApproval } }
   */
  getCutoffEmployees: (filters = {}) =>
    http.get("/dtr/cutoff/employees", { params: clean(filters) }).then(data),
  // → { period, employee, status, approvedBy…, changedAfterApproval, days, totals } (same day shape as My DTR)
  getCutoffEmployeeDays: (employeeId, params = {}) =>
    http
      .get(`/dtr/cutoff/employees/${employeeId}/days`, {
        params: clean(params),
      })
      .then(data),
  // { year, month, cutoff, employeeIds[], remarks?, companyId? } → { period, approved, results: [{ employeeId, ok, status?, error? }] }
  // 400 before the cut-off ends; per-employee errors in results (own DTR, scope, pending requests…)
  approveCutoff: (body) =>
    http.post("/dtr/cutoff/approve", clean(body)).then(data),
  // Same body → { period, unapproved, results } (not after export)
  unapproveCutoff: (body) =>
    http.post("/dtr/cutoff/unapprove", clean(body)).then(data),
  // { year, month, cutoff, employeeIds[], reason (5–500), companyId? } → { period, reopened, results }
  // Company-wide HR only: an exported (processed) cut-off becomes OPEN again to fix it;
  // the next export of those employees is a correction (rows have correctionOf)
  reopenCutoff: (body) =>
    http.post("/dtr/cutoff/reopen", clean(body)).then(data),

  /* ---------- Export (admins/HR) ---------- */
  // POST /dtr/exports { year, month, cutoff, companyId?, branchId?, assignmentType? } → only HR-approved
  // employees; { id, rowCount, exported, skippedNotApproved, alreadyExported }. 400 when nobody is ready.
  createExport: (body) => http.post("/dtr/exports", clean(body)).then(data),
  // Pre-export check (same body + excludeEmployeeIds?) → { period, toExport, excluded,
  // alreadyExported, notApproved: [{ employee, changedAfterApproval }],
  // withIssues: [{ employee, issues: [{ date, weekday, type, attendanceId, firstCheckIn, actions }] }],
  // issueCount, canExport }. type MISSING_CHECKOUT | NO_RECORD.
  checkExport: (body) =>
    http.post("/dtr/exports/check", clean(body)).then(data),
  // { year, month, cutoff, companyId?, fixes: [{ employeeId, date, action,
  //   checkInTime?: "HH:mm", checkOutTime?: "HH:mm", remarks? }] } (max 1000)
  // → { fixed, results: [{ employeeId, date, action, ok, error? }], reapproved }
  resolveExport: (body) =>
    http.post("/dtr/exports/resolve", clean(body)).then(data),
  getExports: (params = {}) =>
    http.get("/dtr/exports", { params: clean(params) }).then(data),
  // CSV as a Blob + the server's file name
  downloadExport: (id) =>
    http
      .get(`/dtr/exports/${id}/download`, { responseType: "blob" })
      .then((r) => ({
        blob: r.data,
        fileName: fileNameFrom(r.headers, `dtr_export_${id}.csv`),
      })),
  // EasyPay payroll import files of a saved export (never change after export):
  // kind "dtr" → EasyPay_DTR_<start>_to_<end>.txt, "overtime" → EasyPay_Overtime_….txt
  // (tab-separated, CRLF, windows-1252; the Blob keeps the server's bytes)
  downloadEasyPay: (id, kind) =>
    http
      .get(`/dtr/exports/${id}/easypay/${kind}.txt`, { responseType: "blob" })
      .then((r) => ({
        blob: r.data,
        fileName: fileNameFrom(r.headers, `EasyPay_${kind}_${id}.txt`),
      })),

  /* ---------- Views ---------- */
  // GET /dtr/weekly?weekStart&… → { weekStart, weekEnd, dates, data: [{ employee, days[7], totals, dtr: [{ id, start, end, status }] }] }
  getWeekly: (filters = {}) =>
    http.get("/dtr/weekly", { params: clean(filters) }).then(data),
  // GET /dtr/entries?from&to&branchId&search&employeeId&page&limit (default current cut-off, max 62 days)
  getEntries: (filters = {}) =>
    http.get("/dtr/entries", { params: clean(filters) }).then(data),
};
